import { useEffect, useRef } from 'react';
import { useUploadStore } from '../store/uploadStore';
import { getUploadFile } from '../services/uploadManager';

// Longest-edge size for generated thumbnails. Covers the 2.75rem modal row and
// the gallery grid tile at high DPR with room to spare.
const THUMB_MAX = 200;
// Decoding (especially HEIF via asm.js) is CPU/memory heavy — keep a tight cap.
const MAX_CONCURRENT = 2;

const inProgress = new Set<string>();
const failed = new Set<string>();
const queue: string[] = [];
let active = 0;

/** Cheap HEIF/HEIC sniff so we only pull in the decoder when it's actually needed. */
function looksLikeHeif(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return true;
  return /\.(heic|heif|hif)$/i.test(file.name);
}

async function decodeToBitmap(file: File): Promise<ImageBitmap> {
  if (looksLikeHeif(file)) {
    // CSP-safe asm.js build — the app's script-src allows neither
    // 'unsafe-eval' nor 'wasm-unsafe-eval', which the default wasm build needs.
    const { heicTo } = await import('heic-to/csp');
    return heicTo({ blob: file, type: 'bitmap' });
  }
  return createImageBitmap(file);
}

async function generate(id: string): Promise<void> {
  const file = getUploadFile(id);
  if (!file) return; // file already released after upload — skip
  const bitmap = await decodeToBitmap(file);
  try {
    const scale = Math.min(1, THUMB_MAX / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bitmap, 0, 0, w, h);
    // data: URL (not blob:) — the CSP img-src allows data: but not blob:.
    const previewUrl = canvas.toDataURL('image/jpeg', 0.72);
    useUploadStore.getState().updateTask(id, { previewUrl });
  } finally {
    bitmap.close();
  }
}

function pump(): void {
  while (active < MAX_CONCURRENT && queue.length > 0) {
    const id = queue.shift()!;
    active++;
    generate(id)
      .catch(() => { failed.add(id); }) // leave the placeholder tile in place
      .finally(() => {
        active--;
        inProgress.delete(id);
        pump();
      });
  }
}

/** Queue lazy thumbnail generation for an upload task. Idempotent. */
export function requestUploadThumbnail(id: string): void {
  if (inProgress.has(id) || failed.has(id)) return;
  const task = useUploadStore.getState().tasks.find((t) => t.id === id);
  if (!task || task.previewUrl) return;
  inProgress.add(id);
  queue.push(id);
  pump();
}

/**
 * Returns a ref to attach to a task's row/tile. The thumbnail is generated the
 * first time the element scrolls near the viewport (then the observer detaches).
 * No-op once a preview already exists.
 */
export function useLazyUploadThumbnail<T extends HTMLElement>(id: string, hasPreview: boolean) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (hasPreview) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestUploadThumbnail(id);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [id, hasPreview]);
  return ref;
}
